import { useState } from 'react'
import { cn } from '@/utils/cn'
import { Clock, FileText, User, Copy, Check, ThumbsUp, ThumbsDown } from 'lucide-react'
import { BrandIcon } from '@/components/ui/BrandIcon'
import { MarkdownContent } from './MarkdownContent'
import { useConversationsStore } from '@/stores/conversationsStore'

interface MessageBubbleProps {
  content: string
  isUser: boolean
  timestamp?: string
  isTyping?: boolean
  typingSeconds?: number
  userAvatar?: string | null
  imageUrl?: string
  isPending?: boolean
  isStreaming?: boolean
  messageId?: string
  conversationId?: string
  feedback?: 'like' | 'dislike' | null
}

// Parse __docs__ marker from content. Returns { text, docs }.
function parseDocContent(raw: string): { text: string; docs: string[] } {
  const marker = '__docs__:'
  const idx = raw.indexOf(marker)
  if (idx === -1) return { text: raw, docs: [] }
  const text = raw.slice(0, idx).replace(/\n+$/, '').trim()
  const docs = raw.slice(idx + marker.length).split('|').map(d => d.trim()).filter(Boolean)
  return { text, docs }
}

// Format timestamps for display. Accepts ISO strings (UTC, like
// "2026-05-06T23:53:00Z") and converts to the user's local time.
// Falls back to the raw value when it can't be parsed (e.g. legacy strings
// already formatted as "HH:MM").
function formatTimestamp(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function MessageBubble({
  content,
  isUser,
  timestamp,
  isTyping = false,
  typingSeconds = 0,
  userAvatar,
  imageUrl,
  isPending = false,
  isStreaming = false,
  messageId,
  conversationId,
  feedback = null,
}: MessageBubbleProps) {
  const { setMessageFeedback } = useConversationsStore()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback silencioso
    }
  }

  const handleFeedback = (value: 'like' | 'dislike') => {
    if (!messageId || !conversationId) return
    // Toggle off se clicar no mesmo
    const next = feedback === value ? null : value
    setMessageFeedback(conversationId, messageId, next)
  }

  return (
    <div className={cn(
      "flex flex-col mb-4 animate-fade-in-up",
      isUser ? "items-end" : "items-start"
    )}>
      <div className={cn(
        "flex gap-3 w-full",
        isUser ? "justify-end" : "justify-start"
      )}>
        {!isUser && (
          <div className="w-9 h-9 bg-brand-accent rounded-xl flex items-center justify-center flex-shrink-0">
            <BrandIcon size={22} className="text-[#0A332F]" />
          </div>
        )}

        <div className={cn(
          "max-w-[75%] rounded-2xl px-4 py-3",
          isUser
            ? "bg-brand-primary text-white dark:text-verde-500 rounded-br-sm"
            : "bg-brand-surface border border-brand-border text-brand-text rounded-bl-sm",
          isTyping && "animate-pulse"
        )}>
          {isTyping ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-brand-text-muted rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-brand-text-muted rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <div className="w-2 h-2 bg-brand-text-muted rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
              <span className="text-xs text-brand-text-muted">
                {typingSeconds < 5 ? 'Pensando...' : typingSeconds < 15 ? 'Processando...' : 'Elaborando resposta...'}
                {typingSeconds > 0 && <span className="opacity-50 ml-1">{typingSeconds}s</span>}
              </span>
            </div>
          ) : (
            <>
              {isUser ? (
                <>
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Imagem enviada"
                      className="max-w-full rounded-lg mb-2 max-h-60 object-contain block"
                      onError={(e) => { e.currentTarget.style.display = 'none' }}
                    />
                  )}
                  {(() => {
                    const { text: parsedText, docs } = parseDocContent(
                      imageUrl ? content.replace(/^📷\s*/, '').trim() : content
                    )
                    return (
                      <>
                        {parsedText && parsedText !== 'Imagem enviada' && (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">{parsedText}</p>
                        )}
                        {docs.length > 0 && (
                          <div className={cn("flex flex-col gap-1", parsedText ? "mt-2" : "")}>
                            {docs.map((name, i) => (
                              <div key={i} className="flex items-center gap-2 bg-white/10 rounded-lg px-2.5 py-1.5">
                                <FileText size={14} className="flex-shrink-0 opacity-80" />
                                <span className="text-xs font-medium truncate max-w-[200px] opacity-90">{name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )
                  })()}
                </>
              ) : (
                <>
                  <MarkdownContent content={content} className="text-sm leading-relaxed" />
                  {isStreaming && content.length > 0 && (
                    <span className="inline-block w-[2px] h-[14px] bg-current opacity-70 ml-0.5 align-middle animate-pulse" />
                  )}
                </>
              )}
              {isPending ? (
                <div className="flex items-center gap-1 mt-1.5 opacity-50">
                  <Clock size={10} />
                  <span className="text-[10px]">Aguardando conexão</span>
                </div>
              ) : timestamp && (
                <p className={cn(
                  "text-[10px] mt-1.5 opacity-60",
                  isUser ? "text-white dark:text-verde-500/70" : "text-brand-text-muted"
                )}>
                  {formatTimestamp(timestamp)}
                </p>
              )}
            </>
          )}
        </div>

        {isUser && (
          <div className="w-9 h-9 bg-brand-primary-light rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
            {userAvatar ? (
              <img
                src={userAvatar}
                alt="User"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <User size={20} className="text-brand-primary" />
            )}
          </div>
        )}
      </div>

      {/* Action bar — shown for all messages except the typing indicator */}
      {!isTyping && (
        <div className={cn(
          "flex items-center gap-0.5 mt-1",
          isUser ? "mr-12" : "ml-12"
        )}>
          <button
            onClick={handleCopy}
            title={isUser ? "Copiar mensagem" : "Copiar resposta"}
            className="p-1.5 rounded-lg text-brand-text-muted hover:text-brand-text hover:bg-brand-border/40 transition-colors"
          >
            {copied
              ? <Check size={14} className="text-brand-accent" />
              : <Copy size={14} />
            }
          </button>
          {!isUser && (
            <>
              <button
                onClick={() => handleFeedback('like')}
                title="Boa resposta"
                className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  feedback === 'like'
                    ? "text-brand-accent"
                    : "text-brand-text-muted hover:text-brand-text hover:bg-brand-border/40"
                )}
              >
                <ThumbsUp size={14} />
              </button>
              <button
                onClick={() => handleFeedback('dislike')}
                title="Resposta ruim"
                className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  feedback === 'dislike'
                    ? "text-red-400"
                    : "text-brand-text-muted hover:text-brand-text hover:bg-brand-border/40"
                )}
              >
                <ThumbsDown size={14} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}